"use client";

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Clock, MessageSquare, Users, RefreshCw } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { ActionButton } from "@/components/common/DataViewTypes";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchMarketingAutomationsRequest,
  deleteMarketingAutomationRequest,
  updateMarketingAutomationRequest,
} from "@/store/slices/marketingAutomation.slice";
import { MarketingAutomation } from "@/types/models/marketing";

const AutomationPage = () => {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmation();
  const [pageIndex, setPageIndex] = useState(0);

  const {
    items: automations,
    loaded,
    loading,
    error,
    pagination,
  } = useSelector((state: RootState) => state.marketingAutomations);

  const fetchAutomations = useCallback(() => {
    dispatch(fetchMarketingAutomationsRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  useEffect(() => {
    if (loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && (automations?.length || 0) === 0) {
      fetchAutomations();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchAutomations();
    }
  }, [loaded, automations?.length, loading, error?.status, pagination?.page, pageIndex, fetchAutomations]);

  const getStatusBadge = (enabled: boolean) => {
    const statusConfig = {
      true: {
        color: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
        label: "Active",
      },
      false: {
        color: "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20",
        label: "Inactive",
      },
    };

    return (
      <Badge
        className={
          statusConfig[enabled.toString() as keyof typeof statusConfig].color
        }
      >
        {statusConfig[enabled.toString() as keyof typeof statusConfig].label}
      </Badge>
    );
  };

  const getChannelBadge = (channel: "sms" | "whatsapp" | "email") => {
    const channelColors = {
      email: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      sms: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
      whatsapp: "bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20",
    };

    const channelLabels = {
      email: "Email",
      sms: "SMS",
      whatsapp: "WhatsApp",
    };

    return (
      <Badge className={channelColors[channel]}>{channelLabels[channel]}</Badge>
    );
  };

  const handleToggleStatus = (
    automationId: string,
    currentEnabled: boolean,
  ) => {
    dispatch(
      updateMarketingAutomationRequest({
        id: automationId,
        data: { enabled: !currentEnabled },
      }),
    );
  };

  const handleDelete = async (automationId: string) => {
    const result = await confirm({
      title: "Delete Automation",
      message:
        "Are you sure you want to delete this automation? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(deleteMarketingAutomationRequest({ id: automationId }));
    }
  };

  const handleEdit = (automationId: string) => {
    navigate(
      `/${storeType}/${storeId}/marketing/automation/${automationId}/edit`,
    );
  };

  const handleRowClick = (automation: MarketingAutomation) => {
    handleEdit(automation.id);
  };

  const handleCreateNew = () => {
    navigate(`/${storeType}/${storeId}/marketing/automation/create`);
  };

  const handleRefresh = () => {
    fetchAutomations();
  };

  const columns: ColumnDef<MarketingAutomation>[] = [
    {
      accessorKey: "name",
      header: "Automation",
      minSize: 300,
      size: 350,
      cell: ({ row }) => {
        const automation = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-foreground">{automation.name}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {automation.trigger_type} •{" "}
                {automation.delay_minutes
                  ? `${automation.delay_minutes}min`
                  : "Instant"}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "channel",
      header: "Channel",
      cell: ({ row }) => {
        return getChannelBadge(row.original.channel);
      },
    },
    {
      accessorKey: "enabled",
      header: "Status",
      cell: ({ row }) => {
        const automation = row.original;
        return (
          <div className="flex items-center gap-3">
            {getStatusBadge(automation.enabled)}
            <Switch
              checked={automation.enabled}
              onCheckedChange={() =>
                handleToggleStatus(automation.id, automation.enabled)
              }
            />
          </div>
        );
      },
    },
    {
      accessorKey: "execution_count",
      header: "Executions",
      cell: ({ row }) => {
        const automation = row.original;
        return (
          <div className="text-sm">
            <div className="flex items-center gap-1 font-medium">
              <Users className="h-3 w-3" />
              {automation.execution_count?.toLocaleString() || 0}
            </div>
            <p className="text-muted-foreground">executions</p>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      cell: ({ row }) => {
        return (
          <div className="text-sm text-muted-foreground">
            {new Date(row.original.updated_at).toLocaleDateString()}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const automation = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEdit(automation.id)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              onClick={() => handleDelete(automation.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "New Automation",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleCreateNew,
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Marketing Automation"
            description="Automate your marketing messages and engage customers at the right time"
            columns={columns}
            data={automations}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={loading}
            emptyMessage="No automations found. Create your first automation to get started."
            actionButtons={actionButtons}
            enableColumnResizing={true}
            onRowClick={handleRowClick}
          />
        </div>
      </TableContainer>
    </div>
  );
};

export default AutomationPage;
